import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  Clock3,
  FileText,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { logClientError } from '@/utils/clientError';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous statuts' },
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Approuvé' },
  { value: 'rejected', label: 'Rejeté' },
];

const STATUS_BADGE_CLASS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
};

const DOCUMENT_LABELS = {
  kyc_document: 'Document entreprise / RCCM',
  kyc_identity: "Pièce d'identité",
  kyc_address_proof: "Preuve d'adresse",
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const getStatusLabel = (status) => STATUS_LABELS[status] || status || '-';

const formatMissingDocuments = (categories = []) => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return '-';
  }
  return categories.map((category) => DOCUMENT_LABELS[category] || category).join(', ');
};

const KYCManagement = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState('');
  const [selectedKycId, setSelectedKycId] = useState('');
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const loadKycDetail = async (kycId) => {
    if (!kycId) {
      setSelectedKyc(null);
      setHistoryItems([]);
      return;
    }

    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/admin/kyc/${kycId}`);
      const nextKyc = response.data?.data?.kyc || null;
      setSelectedKyc(nextKyc);
      setHistoryItems(response.data?.data?.history || []);
      setReviewNotes(nextKyc?.review_notes || '');
      setRejectionReason(nextKyc?.rejection_reason || '');
    } catch (error) {
      logClientError('admin.kyc.load_detail', error);
      toast.error(error.response?.data?.detail || 'Impossible de charger le dossier KYC');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadKycItems = async ({ preferredSelectionId } = {}) => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 200, include_sensitive_documents: true };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get(`${API}/admin/kyc`, { params });
      const nextItems = response.data.items || [];
      setItems(nextItems);

      const resolvedSelectionId = preferredSelectionId || selectedKycId;
      const hasSelection = resolvedSelectionId && nextItems.some((item) => item.id === resolvedSelectionId);
      const nextSelectionId = hasSelection ? resolvedSelectionId : nextItems[0]?.id || '';

      setSelectedKycId(nextSelectionId);
      if (nextSelectionId) {
        await loadKycDetail(nextSelectionId);
      } else {
        setSelectedKyc(null);
        setHistoryItems([]);
      }
    } catch (error) {
      logClientError('admin.kyc.load_items', error);
      toast.error(error.response?.data?.detail || 'Impossible de charger les KYC');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKycItems({ preferredSelectionId: '' });
  }, [statusFilter]);

  const handleSelectKyc = async (kycId) => {
    setSelectedKycId(kycId);
    await loadKycDetail(kycId);
  };

  const handleApprove = async () => {
    if (!selectedKyc?.id || processingAction) {
      return;
    }

    setProcessingAction('approve');
    try {
      await axios.post(`${API}/admin/kyc/${selectedKyc.id}/approve`, {
        notes: reviewNotes || undefined,
      });
      toast.success('KYC approuvé avec succès');
      await loadKycItems({ preferredSelectionId: selectedKyc.id });
    } catch (error) {
      logClientError('admin.kyc.approve', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation KYC");
    } finally {
      setProcessingAction('');
    }
  };

  const handleReject = async () => {
    if (!selectedKyc?.id || processingAction) {
      return;
    }
    if (!String(rejectionReason || '').trim()) {
      toast.error('La raison du rejet est obligatoire.');
      return;
    }

    setProcessingAction('reject');
    try {
      await axios.post(`${API}/admin/kyc/${selectedKyc.id}/reject`, {
        reason: rejectionReason,
      });
      toast.success('KYC rejeté avec succès');
      await loadKycItems({ preferredSelectionId: selectedKyc.id });
    } catch (error) {
      logClientError('admin.kyc.reject', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet KYC');
    } finally {
      setProcessingAction('');
    }
  };

  const isPending = selectedKyc?.status === 'pending';
  const statusClass = STATUS_BADGE_CLASS[selectedKyc?.status] || 'bg-slate-100 text-slate-700';
  const summaryCounts = useMemo(
    () => ({
      pending: items.filter((item) => item.status === 'pending').length,
      approved: items.filter((item) => item.status === 'approved').length,
      rejected: items.filter((item) => item.status === 'rejected').length,
    }),
    [items]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <ShieldCheck className="h-6 w-6 text-red-600" />
                Gestion KYC vendeurs
              </CardTitle>
              <CardDescription>
                Consultez un vrai dossier vendeur, visualisez les pièces et validez ou rejetez avec contexte.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => loadKycItems({ preferredSelectionId: selectedKycId })}
              disabled={loading || detailLoading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${(loading || detailLoading) ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs uppercase tracking-wide text-amber-700">En attente</p>
              <p className="mt-1 text-2xl font-semibold text-amber-900">{summaryCounts.pending}</p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs uppercase tracking-wide text-green-700">Approuvés</p>
              <p className="mt-1 text-2xl font-semibold text-green-900">{summaryCounts.approved}</p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs uppercase tracking-wide text-red-700">Rejetés</p>
              <p className="mt-1 text-2xl font-semibold text-red-900">{summaryCounts.rejected}</p>
            </div>
            <div className="max-w-xs">
              <Label className="mb-1 block">Filtrer par statut</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-500">Chargement des KYC...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-gray-500">Aucune demande KYC pour ce filtre.</div>
          ) : (
            <div className="rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendeur</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Soumis le</TableHead>
                    <TableHead>Dossier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const rowStatusClass = STATUS_BADGE_CLASS[item.status] || 'bg-slate-100 text-slate-700';
                    const isSelected = item.id === selectedKycId;
                    return (
                      <TableRow key={item.id} className={isSelected ? 'bg-cyan-50/60' : ''}>
                        <TableCell className="font-medium">{item.vendor_name || '-'}</TableCell>
                        <TableCell>{item.org_name || item.org_id}</TableCell>
                        <TableCell>{item.business_name || '-'}</TableCell>
                        <TableCell>
                          <Badge className={rowStatusClass}>{getStatusLabel(item.status)}</Badge>
                        </TableCell>
                        <TableCell>{item.version || 1}</TableCell>
                        <TableCell>{formatDateTime(item.submitted_at)}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant={isSelected ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleSelectKyc(item.id)}
                          >
                            Voir le dossier
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-cyan-600" />
                Dossier détaillé
              </CardTitle>
              <CardDescription>
                Informations vendeur, justificatifs sécurisés et historique minimal de traitement.
              </CardDescription>
            </div>
            {selectedKyc && <Badge className={statusClass}>{getStatusLabel(selectedKyc.status)}</Badge>}
          </div>
        </CardHeader>
        <CardContent>
          {detailLoading ? (
            <div className="py-10 text-center text-gray-500">Chargement du dossier...</div>
          ) : !selectedKyc ? (
            <div className="py-10 text-center text-gray-500">Sélectionnez un dossier KYC pour afficher le détail.</div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Vendeur</p>
                  <p className="mt-1 font-semibold text-slate-900">{selectedKyc.vendor_name || '-'}</p>
                  <p className="text-sm text-slate-600">{selectedKyc.vendor_email || 'Email non renseigné'}</p>
                  <p className="text-sm text-slate-600">{selectedKyc.vendor_phone || 'Téléphone non renseigné'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Organisation / boutique</p>
                  <p className="mt-1 font-semibold text-slate-900">{selectedKyc.org_name || selectedKyc.org_id}</p>
                  <p className="text-sm text-slate-600">Version {selectedKyc.version || 1}</p>
                  <p className="text-sm text-slate-600">Soumis le {formatDateTime(selectedKyc.submitted_at)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Vérification</p>
                  <p className="mt-1 font-semibold text-slate-900">{getStatusLabel(selectedKyc.status)}</p>
                  <p className="text-sm text-slate-600">
                    Revu le : {selectedKyc.reviewed_at ? formatDateTime(selectedKyc.reviewed_at) : 'Non traité'}
                  </p>
                  <p className="text-sm text-slate-600">Admin : {selectedKyc.reviewed_by_admin_id || '-'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Complétude</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {selectedKyc.is_complete ? 'Dossier complet' : 'Pièces manquantes'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {selectedKyc.is_complete
                      ? 'Les pièces obligatoires sont présentes.'
                      : formatMissingDocuments(selectedKyc.missing_document_categories)}
                  </p>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[2fr,1fr]">
                <div className="space-y-6">
                  <div className="rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-900">Informations entreprise</h3>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Nom légal</p>
                        <p className="font-medium text-slate-900">{selectedKyc.business_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Nature d'activité</p>
                        <p className="font-medium text-slate-900">{selectedKyc.business_activity || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Type de document entreprise</p>
                        <p className="font-medium text-slate-900">{selectedKyc.document_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">RCCM / équivalent</p>
                        <p className="font-medium text-slate-900">{selectedKyc.registration_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">NIU / identifiant fiscal</p>
                        <p className="font-medium text-slate-900">{selectedKyc.tax_identifier || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Contact professionnel</p>
                        <p className="font-medium text-slate-900">{selectedKyc.business_contact_phone || '-'}</p>
                        <p className="text-sm text-slate-600">
                          {selectedKyc.business_contact_email || 'Email non renseigné'}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Adresse professionnelle</p>
                        <p className="font-medium text-slate-900">{selectedKyc.professional_address || '-'}</p>
                        <p className="text-sm text-slate-600">
                          {[selectedKyc.business_city, selectedKyc.business_country].filter(Boolean).join(', ')
                            || 'Ville / pays non renseignés'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-900">Pièces justificatives</h3>
                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      {(selectedKyc.documents || []).map((documentItem) => (
                        <div key={documentItem.category} className="rounded-md border border-slate-200 p-3 text-sm">
                          <p className="font-medium text-slate-900">
                            {DOCUMENT_LABELS[documentItem.category] || documentItem.label || documentItem.category}
                          </p>
                          <p className="text-slate-500">{documentItem.original_filename || 'Document sécurisé'}</p>
                          {documentItem.access_url ? (
                            <a
                              href={documentItem.access_url}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-cyan-700 underline"
                            >
                              <FileText className="h-4 w-4" />
                              Ouvrir
                            </a>
                          ) : (
                            <span className="mt-2 inline-block text-xs text-slate-500">Document indisponible</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {Array.isArray(selectedKyc.document_checklist) && selectedKyc.document_checklist.length > 0 && (
                      <div className="mt-4 grid gap-3 md:grid-cols-3">
                        {selectedKyc.document_checklist.map((item) => (
                          <div
                            key={`doc-check-${item.category}`}
                            className={`rounded-md border p-3 text-sm ${
                              item.is_present
                                ? 'border-green-200 bg-green-50 text-green-800'
                                : 'border-amber-200 bg-amber-50 text-amber-800'
                            }`}
                          >
                            <p className="font-medium">{item.label || DOCUMENT_LABELS[item.category] || item.category}</p>
                            <p className="mt-1 text-xs">{item.is_present ? 'Document présent' : 'Document manquant'}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-900">Notes et décision</h3>
                    {selectedKyc.seller_notes ? (
                      <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                        <strong>Notes vendeur :</strong> {selectedKyc.seller_notes}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-slate-500">Aucune note vendeur.</p>
                    )}

                    {selectedKyc.review_notes && (
                      <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                        <strong>Notes admin :</strong> {selectedKyc.review_notes}
                      </div>
                    )}

                    {selectedKyc.rejection_reason && (
                      <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800">
                        <strong>Motif de rejet :</strong> {selectedKyc.rejection_reason}
                      </div>
                    )}

                    {isPending && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <Label>Note admin (approbation)</Label>
                          <Textarea
                            value={reviewNotes}
                            onChange={(event) => setReviewNotes(event.target.value)}
                            placeholder="Commentaire interne ou retour vendeur"
                            rows={3}
                            disabled={Boolean(processingAction)}
                          />
                        </div>
                        <div>
                          <Label>Raison du rejet</Label>
                          <Textarea
                            value={rejectionReason}
                            onChange={(event) => setRejectionReason(event.target.value)}
                            placeholder="Expliquez clairement ce qui doit être corrigé"
                            rows={4}
                            disabled={Boolean(processingAction)}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={handleApprove}
                            disabled={Boolean(processingAction)}
                          >
                            <CheckCircle2 className="mr-1 h-4 w-4" />
                            {processingAction === 'approve' ? 'Approbation...' : 'Approuver'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={handleReject}
                            disabled={Boolean(processingAction)}
                          >
                            <XCircle className="mr-1 h-4 w-4" />
                            {processingAction === 'reject' ? 'Rejet...' : 'Rejeter'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 p-4">
                    <h3 className="font-semibold text-slate-900">Historique minimal</h3>
                    {historyItems.length === 0 ? (
                      <p className="mt-3 text-sm text-slate-500">Aucun historique disponible.</p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {historyItems.map((historyItem) => {
                          const historyStatusClass = STATUS_BADGE_CLASS[historyItem.status] || 'bg-slate-100 text-slate-700';
                          return (
                            <div key={historyItem.id} className="rounded-md border border-slate-200 p-3 text-sm">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="font-medium text-slate-900">Version {historyItem.version || 1}</p>
                                  <p className="text-slate-500">Soumis le {formatDateTime(historyItem.submitted_at)}</p>
                                </div>
                                <Badge className={historyStatusClass}>{getStatusLabel(historyItem.status)}</Badge>
                              </div>
                              <p className="mt-2 text-xs text-slate-500">
                                Revu le : {historyItem.reviewed_at ? formatDateTime(historyItem.reviewed_at) : 'Non traité'}
                              </p>
                              {historyItem.is_current && (
                                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-700">
                                  <Clock3 className="h-3.5 w-3.5" />
                                  Dossier courant
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedKyc.status === 'approved' && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                  <strong>Badge vendeur vérifié actif.</strong> Le vendeur est actuellement considéré comme vérifié
                  selon le KYC approuvé.
                </div>
              )}

              {selectedKyc.status === 'rejected' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    <div>
                      <p className="font-semibold">Resoumission attendue</p>
                      <p>Le vendeur doit corriger son dossier et le resoumettre. La raison du rejet reste visible dans son espace.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KYCManagement;
